import React, { useState, useEffect } from 'react';
import {
    BarChart3, Award, Target, AlertCircle, Loader2
} from 'lucide-react';
import analyticsService from '../../services/analyticsService';

const AnalyticsDashboard = ({ courseId, artifactCount }) => {
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingAnalysis, setLoadingAnalysis] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [recommendations, setRecommendations] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!courseId) return;

            // 1. Fetch Basic Stats (Fast)
            setLoadingStats(true);
            try {
                const analyticsData = await analyticsService.getCourseAnalytics(courseId);
                if (analyticsData.success) {
                    setAnalytics(analyticsData.analytics);
                }
            } catch (err) {
                console.error("Error loading analytics:", err);
                // Don't error out completely, maybe just stats failed
            } finally {
                setLoadingStats(false);
            }

            // 2. Fetch Performance Analysis (Slow)
            setLoadingAnalysis(true);
            try {
                console.log('🤖 Starting AI analysis...');
                const analysisData = await analyticsService.getPerformanceAnalysis(courseId);
                if (analysisData.success && analysisData.analysis) {
                    console.log('✅ Analysis loaded:', analysisData.analysis);
                    setRecommendations(analysisData.analysis);
                }
            } catch (err) {
                console.error("Error loading performance analysis:", err);
            } finally {
                setLoadingAnalysis(false);
            }
        };

        fetchAnalytics();
    }, [courseId]);

    if (loadingStats) return (
        <div className="space-y-6 animate-pulse">
            <div className="bg-white rounded-xl p-6 shadow-lg h-64"></div>
            <div className="bg-white rounded-xl p-6 shadow-lg h-48"></div>
        </div>
    );

    if (error) return <div className="p-4 text-red-500 bg-red-50 rounded-lg">{error}</div>;

    // Default fallback values if API returns null
    const safeAnalytics = analytics || {
        quiz_average: 0,
        completed_tests: 0,
        streak_days: 0,
        course_completion: 0,
        strengths: [],
        improvements: []
    };

    return (
        <div className="space-y-6">
            {/* Analytics Dettagliate */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                    Analytics Dettagliate
                </h2>
                <div className="grid grid-cols-2 gap-4">
                    {[
                        {
                            label: 'Percentuale di superamento dei quiz',
                            value: `${safeAnalytics.quiz_average}%`,
                            percent: safeAnalytics.quiz_average,
                            color: 'blue'
                        },
                        {
                            label: 'Completamento Corso',
                            value: `${safeAnalytics.course_completion || 0}%`,
                            percent: safeAnalytics.course_completion || 0,
                            color: 'green'
                        },
                        {
                            label: 'Contenuti Generati',
                            value: `${artifactCount !== undefined ? artifactCount : safeAnalytics.completed_tests}`,
                            color: 'purple'
                        },
                        {
                            label: 'Streak Giorni',
                            value: `${safeAnalytics.streak_days} giorni`,
                            color: 'yellow'
                        }
                    ].map((stat, idx) => {
                        const textColors = {
                            blue: 'text-blue-600',
                            green: 'text-green-600',
                            purple: 'text-purple-600',
                            yellow: 'text-yellow-600',
                        };
                        const bgColors = {
                            blue: 'bg-blue-600',
                            green: 'bg-green-600',
                            purple: 'bg-purple-600',
                            yellow: 'bg-yellow-600',
                        };

                        return (
                            <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                                <div className="text-sm text-gray-600 mb-2">{stat.label}</div>
                                <div className={`text-3xl font-bold ${textColors[stat.color] || 'text-gray-800'} mb-2`}>{stat.value}</div>
                                {stat.percent !== undefined && (
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full ${bgColors[stat.color] || 'bg-blue-600'}`}
                                            style={{ width: `${stat.percent}%` }}
                                        ></div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Punti di Forza (AI Analysis) */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Award className="w-6 h-6 text-green-600" />
                    Punti di Forza (AI)
                    {loadingAnalysis && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </h2>
                <div className="space-y-3">
                    {loadingAnalysis ? (
                        // Skeleton Loading for Analysis
                        <div className="space-y-3 animate-pulse">
                            <div className="h-20 bg-green-50 rounded-lg"></div>
                            <div className="h-20 bg-green-50 rounded-lg"></div>
                        </div>
                    ) : recommendations?.strong_points && recommendations.strong_points.length > 0 ? (
                        recommendations.strong_points.map((item, idx) => (
                            <div key={idx} className="p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                                <div className="font-bold text-gray-800 mb-1">{item.topic}</div>
                                <p className="text-sm text-green-800">{item.description}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 italic">Analisi in corso o dati non sufficienti...</p>
                    )}
                </div>
            </div>

            {/* Aree di Miglioramento / Raccomandazioni (AI Analysis) */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Target className="w-6 h-6 text-red-600" />
                    Consigli AI & Miglioramenti
                    {loadingAnalysis && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </h2>

                <div className="space-y-4">
                    {loadingAnalysis ? (
                        // Skeleton Loading for Recommendations
                        <div className="space-y-4 animate-pulse">
                            <div className="h-24 bg-yellow-50 rounded-lg"></div>
                            <div className="h-24 bg-yellow-50 rounded-lg"></div>
                        </div>
                    ) : recommendations?.recommendations && recommendations.recommendations.length > 0 ? (
                        recommendations.recommendations.map((item, idx) => (
                            <div key={idx} className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
                                <div className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                                    {item.topic}
                                </div>
                                <p className="text-sm text-gray-700">{item.advice}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 italic">Inizia a completare qualche quiz per permettermi di analizzare il tuo andamento!</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
